import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { ReservationPricing, SellLimitStatistic } from '@mews-distributor/data-access';
import { FETCH_RESERVATION_PRICING_SUCCEEDED } from '../entities/reservationPrices/actionTypes';
import { fetchServicePricingSucceeded } from '../servicePricing/reducers';
import { getSellLimitHash } from './hash';

export interface RateSellLimit extends SellLimitStatistic {
    rateId: string;
    categoryId: string;
}

export type SellLimitSlice = Record<string, Array<RateSellLimit>>;

const dataInitialState: SellLimitSlice = {};

interface FetchReservationPricingPayload extends ReservationPricing {
    hotelId: string;
    startDate: string;
    endDate: string;
    productIds: string[];
    roomId: string;
}

export const sellLimitsReducer = createReducer(dataInitialState, (builder) => {
    builder.addCase(
        FETCH_RESERVATION_PRICING_SUCCEEDED,
        (
            state: SellLimitSlice,
            {
                payload: { startDate, endDate, roomId: categoryId, occupancyPrices },
            }: PayloadAction<FetchReservationPricingPayload, typeof FETCH_RESERVATION_PRICING_SUCCEEDED>
        ) => {
            const pricingValue = occupancyPrices[0]?.pricing ?? [];
            const hash = getSellLimitHash({ startDate, endDate });
            state[hash] = pipe(
                pricingValue,
                array.filterMap(({ rateId, sellLimitStatistic }) => {
                    if (sellLimitStatistic == null || rateId == null) {
                        return option.none;
                    }
                    return option.some({
                        rateId,
                        categoryId,
                        limit: sellLimitStatistic.limit,
                        available: sellLimitStatistic.available,
                    });
                })
            );
        }
    );
    builder.addCase(fetchServicePricingSucceeded, (state, { payload: { categoryPrices, startDate, endDate } }) => {
        const hash = getSellLimitHash({ startDate, endDate });

        state[hash] = pipe(
            categoryPrices,
            array.flatMap(({ categoryId, occupancyPrices }) => {
                if (occupancyPrices[0] == null) {
                    return [];
                }

                const { rateGroupPrices } = occupancyPrices[0];
                return pipe(
                    rateGroupPrices,
                    array.filterMap(({ minRateId, sellLimitStatistic }) => {
                        if (sellLimitStatistic == null) {
                            return option.none;
                        }
                        return option.some({
                            rateId: minRateId,
                            categoryId,
                            ...sellLimitStatistic,
                        });
                    })
                );
            })
        );
    });
});
